import { CLASSICAL_SCORES } from './classicalScores';

const STORAGE_KEY = 'celebrations_audio_settings';
const DEFAULT_SETTINGS = { music: false, soundEffects: true };

let settings = { ...DEFAULT_SETTINGS };
let audioContext = null;
let masterGain = null;
let musicTimer = null;
const listeners = new Set();

function readSettings() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      music: typeof stored.music === 'boolean' ? stored.music : DEFAULT_SETTINGS.music,
      soundEffects: typeof stored.soundEffects === 'boolean'
        ? stored.soundEffects
        : DEFAULT_SETTINGS.soundEffects,
    };
  } catch (error) {
    console.warn('Could not read audio preferences:', error);
    return { ...DEFAULT_SETTINGS };
  }
}

settings = readSettings();

function getAudioContext() {
  if (audioContext) return audioContext;
  try {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return null;

    audioContext = new AudioContextConstructor();
    masterGain = audioContext.createGain();
    masterGain.gain.value = 0.7;
    masterGain.connect(audioContext.destination);
    return audioContext;
  } catch (error) {
    console.warn('Could not create game audio:', error);
    return null;
  }
}

function playTone(frequency, duration, waveform = 'sine', volume = 0.18, endFrequency = frequency) {
  const context = getAudioContext();
  if (!context || !masterGain || context.state !== 'running') return;

  const oscillator = context.createOscillator();
  const gain = context.createGain();
  const now = context.currentTime;
  oscillator.type = waveform;
  oscillator.frequency.setValueAtTime(frequency, now);
  oscillator.frequency.exponentialRampToValueAtTime(Math.max(1, endFrequency), now + duration);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(volume, now + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  oscillator.connect(gain);
  gain.connect(masterGain);
  oscillator.start(now);
  oscillator.stop(now + duration + 0.02);
}

const DEFAULT_SCENE = 'multiplayer';
let musicScene = DEFAULT_SCENE;
let nextLoopTime = 0;
const musicVoices = new Set();

const NOTE_OFFSETS = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// 'C#4' -> 277.18 Hz; 'R' (rest) -> null
function noteFrequency(name) {
  if (name === 'R') return null;
  const [, letter, accidental, octave] = /^([A-G])([#b]?)(-?\d)$/.exec(name) || [];
  if (!letter) return null;
  const semitone = NOTE_OFFSETS[letter] + (accidental === '#' ? 1 : accidental === 'b' ? -1 : 0);
  return 440 * 2 ** ((semitone + (Number(octave) + 1) * 12 - 69) / 12);
}

// Schedules one soft piano-like note on the audio clock and tracks it so it can be cancelled.
function scheduleVoice(frequency, start, duration, waveform, volume) {
  const oscillator = audioContext.createOscillator();
  const gain = audioContext.createGain();
  const end = start + duration;
  oscillator.type = waveform;
  oscillator.frequency.setValueAtTime(frequency, start);
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(volume, start + 0.02);
  gain.gain.exponentialRampToValueAtTime(volume * 0.5, start + duration * 0.6);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);
  oscillator.connect(gain);
  gain.connect(masterGain);
  oscillator.start(start);
  oscillator.stop(end + 0.05);
  musicVoices.add(oscillator);
  oscillator.onended = () => musicVoices.delete(oscillator);
}

function stopMusicVoices() {
  musicVoices.forEach((oscillator) => {
    try { oscillator.stop(); } catch { /* already stopped */ }
  });
  musicVoices.clear();
}

function stopMusic() {
  window.clearTimeout(musicTimer);
  musicTimer = null;
  stopMusicVoices();
}

// Plays one pass of the current section's piece, then schedules the next pass just before it ends.
function scheduleMusicLoop() {
  if (!settings.music || !audioContext || audioContext.state !== 'running') return;

  const { bpm, beatsPerBar, wave, volume, melody, bass } = CLASSICAL_SCORES[musicScene];
  const secondsPerBeat = 60 / bpm;
  const start = Math.max(nextLoopTime, audioContext.currentTime + 0.1);

  let beat = 0;
  melody.forEach(([name, beats]) => {
    const frequency = noteFrequency(name);
    if (frequency) scheduleVoice(frequency, start + beat * secondsPerBeat, beats * secondsPerBeat * 0.95, wave, volume);
    beat += beats;
  });

  const bars = Math.ceil(beat / beatsPerBar);
  const half = beatsPerBar / 2;
  for (let bar = 0; bar < bars; bar += 1) {
    const frequency = noteFrequency(bass[bar % bass.length]);
    [0, half].forEach((offset) => {
      scheduleVoice(frequency, start + (bar * beatsPerBar + offset) * secondsPerBeat, half * secondsPerBeat * 0.95, 'sine', volume * 0.8);
    });
  }

  const loopSeconds = bars * beatsPerBar * secondsPerBeat;
  nextLoopTime = start + loopSeconds;
  const delay = Math.max(50, (nextLoopTime - audioContext.currentTime - 0.5) * 1000);
  musicTimer = window.setTimeout(scheduleMusicLoop, delay);
}

function startMusic() {
  stopMusic();
  nextLoopTime = 0;
  if (settings.music && audioContext?.state === 'running') scheduleMusicLoop();
}

export function setMusicScene(scene) {
  const next = CLASSICAL_SCORES[scene] ? scene : DEFAULT_SCENE;
  if (next === musicScene) return;
  musicScene = next;
  if (musicTimer !== null) startMusic();
}

function saveSettings() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch (error) {
    console.warn('Could not save audio preferences:', error);
  }
  listeners.forEach(listener => listener(settings));
}

export function getAudioSettings() {
  return { ...settings };
}

export function subscribeAudioSettings(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setAudioSetting(key, value) {
  settings = { ...settings, [key]: value };
  saveSettings();
  if (key === 'music') {
    if (value) startMusic();
    else stopMusic();
  }
}

export async function unlockGameAudio() {
  const context = getAudioContext();
  if (!context) return false;
  try {
    if (context.state !== 'running') {
      // Without a user gesture resume() stays pending instead of rejecting, so don't wait on it forever.
      await Promise.race([context.resume(), new Promise(resolve => window.setTimeout(resolve, 300))]);
    }
    const running = context.state === 'running';
    if (running && settings.music && musicTimer === null) startMusic();
    return running;
  } catch (error) {
    console.warn('Could not start game audio:', error);
    return false;
  }
}

export function playGameSound(name) {
  if (!settings.soundEffects || !audioContext || audioContext.state !== 'running') return;

  const sounds = {
    tap: () => playTone(520, 0.055, 'triangle', 0.055, 430),
    countdown: () => playTone(740, 0.1, 'sine', 0.1, 620),
    start: () => playTone(392, 0.18, 'triangle', 0.12, 587.33),
    move: () => playTone(330, 0.075, 'triangle', 0.09, 392),
    score: () => playTone(660, 0.09, 'sine', 0.075, 740),
    flap: () => playTone(620, 0.08, 'triangle', 0.11, 790),
    roll: () => playTone(180, 0.22, 'triangle', 0.16, 620),
    card: () => playTone(460, 0.11, 'square', 0.075, 320),
    line: () => {
      playTone(523.25, 0.2, 'triangle', 0.12);
      window.setTimeout(() => playTone(783.99, 0.24, 'triangle', 0.1), 75);
    },
    correct: () => {
      playTone(523.25, 0.18, 'triangle', 0.14);
      window.setTimeout(() => playTone(659.25, 0.2, 'triangle', 0.14), 90);
      window.setTimeout(() => playTone(783.99, 0.28, 'triangle', 0.13), 180);
    },
    wrong: () => playTone(240, 0.22, 'triangle', 0.12, 145),
    crash: () => playTone(190, 0.42, 'sawtooth', 0.18, 48),
    win: () => {
      playTone(523.25, 0.2, 'triangle', 0.15);
      window.setTimeout(() => playTone(659.25, 0.2, 'triangle', 0.15), 100);
      window.setTimeout(() => playTone(783.99, 0.2, 'triangle', 0.15), 200);
      window.setTimeout(() => playTone(1046.5, 0.42, 'triangle', 0.14), 300);
    },
  };
  sounds[name]?.();
}

export function resumeGameAudio() {
  return unlockGameAudio();
}

export function pauseGameAudio() {
  stopMusic();
  if (audioContext?.state === 'running') {
    audioContext.suspend().catch(error => console.warn('Could not pause game audio:', error));
  }
}
